/**
 * Builds the shared `{ triggerType, useRegex }` context consumed by the
 * URL-validation helpers below.
 *
 * @param {string} triggerType - Current trigger type.
 * @param {boolean} useRegex - Whether the trigger uses regex matching.
 * @returns {{ triggerType: string, useRegex: boolean }} Validation context.
 * @example
 * const context = buildValidationContext("web", true);
 */
export function buildValidationContext(triggerType, useRegex) {
  return { triggerType, useRegex };
}

/**
 * Validates a trigger URL-like field (path/query/hash/host) against the
 * character rules for the current trigger type, or as a regex when the
 * trigger uses regex matching.
 *
 * @param {string} str - Value to validate.
 * @param {boolean} useStrict - Use the stricter character set (blocks `?&=` too).
 * @param {{ triggerType: string, useRegex: boolean }} context - Trigger type and regex flag.
 * @returns {boolean} Whether the value is valid.
 * @example
 * isValidUrl("/checkout", false, { triggerType: "web", useRegex: false }); // true
 */
export function isValidUrl(str, useStrict, { triggerType, useRegex } = {}) {
  if (!str) return true;

  const isDevice = triggerType === "device";
  if (useRegex) {
    try {
      return !!new RegExp(str, "i");
    } catch (_) {
      return false;
    }
  }

  const strictPattern = isDevice ? /[\[\]{}"%~#<>?&=\t\r\n\f\v]/ : /[\[\]{}\s"%~#<>?&=]/;
  const relaxedPattern = isDevice ? /[\[\]{}"%~#<>\t\r\n\f\v]/ : /[\[\]{}\s"%~#<>]/;

  return !(useStrict ? strictPattern : relaxedPattern).test(str);
}

/**
 * `isValidUrl` with `useStrict: true`, returning a form error message instead of `false`.
 *
 * @param {string} str - Value to validate.
 * @param {{ triggerType: string, useRegex: boolean }} context - Trigger type and regex flag.
 * @returns {true|string} `true` when valid, otherwise an error message.
 * @example
 * isValidUrlRuleStrict("bad?url", { triggerType: "web", useRegex: false }); // "Invalid characters"
 */
export function isValidUrlRuleStrict(str, context = {}) {
  return (
    isValidUrl(str, true, context) ||
    `Invalid ${context.useRegex ? "Regular expression" : "characters"}`
  );
}

/**
 * `isValidUrl` with `useStrict: false`, returning a form error message instead of `false`.
 *
 * @param {string} str - Value to validate.
 * @param {{ triggerType: string, useRegex: boolean }} context - Trigger type and regex flag.
 * @returns {true|string} `true` when valid, otherwise an error message.
 * @example
 * isValidUrlRule("/checkout?ref=1", { triggerType: "web", useRegex: false }); // true
 */
export function isValidUrlRule(str, context = {}) {
  return (
    isValidUrl(str, false, context) ||
    `Invalid ${context.useRegex ? "Regular expression" : "characters"}`
  );
}

/**
 * Resolves the error message for a URL-like field, or `null` when it's valid.
 *
 * @param {string} str - Value to validate.
 * @param {boolean} [useStrict=false] - Use the stricter character set.
 * @param {{ triggerType: string, useRegex: boolean }} context - Trigger type and regex flag.
 * @returns {string|null} Error message, or `null` when valid.
 * @example
 * urlFieldErrorMessage("bad?url", true, { triggerType: "web", useRegex: false }); // "Invalid characters"
 */
export function urlFieldErrorMessage(str, useStrict = false, context = {}) {
  const result = useStrict ? isValidUrlRuleStrict(str, context) : isValidUrlRule(str, context);
  return result === true ? null : result;
}

/**
 * Preset value items for an `RfSelect` filter-builder row — the app's domain and its
 * aliases for `url_host` rows, every value ever added to the row for free-text rows
 * (`availableValues`, not just the currently-checked `values`) so unchecking one doesn't
 * drop it from the list of options.
 *
 * @param {{ filterType: string, availableValues?: string[] }} row - Filter row.
 * @param {{ domain?: string, domain_aliases?: string[] }|null} app - App the trigger belongs to.
 * @returns {string[]} Flat `items` for `RfSelect`.
 * @example
 * rowValueItems({ filterType: "url_host" }, { domain: "example.com" });
 * // ["example.com"]
 */
export function rowValueItems(row, app) {
  return row.filterType === "url_host"
    ? [app?.domain, ...(app?.domain_aliases || [])].filter(Boolean)
    : row.availableValues || [];
}

/**
 * Whether the trigger's classic-mode fields (or filter-builder rows) are all valid.
 *
 * @param {object} params
 * @param {boolean} params.filterModeDisabled - `true` when classic mode is active.
 * @param {object} params.editingTrigger - Trigger being edited.
 * @param {string} params.triggerType - Current trigger type.
 * @returns {boolean} Whether the form is valid.
 * @example
 * isTriggerFormValid({ filterModeDisabled: true, editingTrigger, triggerType: "web" });
 */
export function isTriggerFormValid({ filterModeDisabled, editingTrigger, triggerType }) {
  if (!filterModeDisabled) {
    return true;
  }
  const context = buildValidationContext(triggerType, editingTrigger.use_regex);
  return (
    isValidUrl(editingTrigger.url_path, true, context) &&
    isValidUrl(editingTrigger.query_params, false, context) &&
    isValidUrl(editingTrigger.url_hash, false, context) &&
    isValidUrl(editingTrigger.url_host, false, context)
  );
}

/**
 * Resolves the disabled/tooltip message for the dialog's confirm button.
 *
 * @param {object} params
 * @param {string|false} params.isDisabledWithMessage - Read-only reason from `userStore`, if any.
 * @param {{ name?: string }|null} params.editingTrigger - Trigger being edited.
 * @param {boolean} params.submitEnabled - Whether all other requirements are met.
 * @returns {string|false} A message to disable with, `""` to disable silently, or `false` to enable.
 * @example
 * confirmDisabledMessage({ isDisabledWithMessage: false, editingTrigger: { name: "" }, submitEnabled: true });
 * // "Name is required"
 */
export function confirmDisabledMessage({ isDisabledWithMessage, editingTrigger, submitEnabled }) {
  if (isDisabledWithMessage) return isDisabledWithMessage;
  if (!editingTrigger?.name?.trim()) return "Name is required";
  return submitEnabled ? false : "";
}

/**
 * Decides what should happen when the "Classic view" switch is toggled.
 *
 * @param {object} params
 * @param {boolean} params.newValue - The switch's new (requested) state.
 * @param {boolean} params.isFilterOptionsEmpty - Whether the filter builder has no filters set.
 * @returns {"enableFilterBuilder"|"enableClassicView"|"confirmClassicView"} Action to take.
 * @example
 * resolveFilterModeToggle({ newValue: true, isFilterOptionsEmpty: false }); // "confirmClassicView"
 */
export function resolveFilterModeToggle({ newValue, isFilterOptionsEmpty }) {
  if (!newValue) return "enableFilterBuilder";
  if (isFilterOptionsEmpty) return "enableClassicView";
  return "confirmClassicView";
}
